<template>
    <v-card class="add-duck-form pa-4">
        <v-card-title class="text-h6 mb-4 sticky-header">Add Duck(s)</v-card-title>
        
        <!-- Warning Message -->
        <v-alert
          v-if="props.warningMessage"
          type="warning"
          variant="tonal"
          border="start"
          class="mb-4"
          icon="mdi-alert-circle-outline"
          closable
        >
          {{ props.warningMessage }}
        </v-alert>
        
        <!-- Main scrollable content -->
        <div class="form-scroll-container">
          <v-form @submit.prevent="handleSubmit" class="form-content">
              <!-- Duck Creation Mode -->
              <v-btn-toggle
                  v-model="creationMode"
                  color="primary"
                  mandatory
                  variant="outlined"
                  class="mb-4 w-full toggle-container"
              >
                  <v-btn value="single" class="w-1/2 toggle-btn">
                      <v-icon start>mdi-duck</v-icon>
                      Single Duck
                  </v-btn>
                  <v-btn value="multiple" class="w-1/2 toggle-btn">
                      <div class="flex items-center justify-center">
                          <v-icon>mdi-layers-plus</v-icon>
                          <span class="ml-2">Multiple Ducks</span>
                      </div>
                  </v-btn>
              </v-btn-toggle>

              <!-- Image Selection (Visual Toggle) -->
              <div class="mb-4">
                  <p class="text-body-1 font-medium mb-2">Duck Image:</p>
                  <div class="image-selector-container">
                      <div 
                          class="image-option" 
                          :class="{'selected': useDefaultImage}"
                          @click="useDefaultImage = true"
                      >
                          <div class="image-preview">
                              <img src="/ducksImages/duckIcon.webp" alt="Default Duck" class="preview-image" />
                          </div>
                          <div class="image-label">
                              <v-icon v-if="useDefaultImage" color="primary" class="mr-1">mdi-check-circle</v-icon>
                              Default Duck
                          </div>
                      </div>
                      
                      <div 
                          class="image-option" 
                          :class="{'selected': !useDefaultImage}"
                          @click="useDefaultImage = false"
                      >
                          <div class="image-preview">
                              <div v-if="imageFile && imagePreview" class="custom-preview">
                                  <img :src="imagePreview" alt="Custom Duck" class="preview-image" />
                              </div>
                              <div v-else class="upload-placeholder">
                                  <v-icon size="40" color="grey-lighten-1">mdi-file-image-plus</v-icon>
                              </div>
                          </div>
                          <div class="image-label">
                              <v-icon v-if="!useDefaultImage" color="primary" class="mr-1">mdi-check-circle</v-icon>
                              Custom Image
                          </div>
                      </div>
                  </div>
                  
                  <!-- File Input (only shown when custom image is selected) -->
                  <v-slide-y-transition>
                      <v-file-input
                          v-if="!useDefaultImage"
                          v-model="imageFile"
                          accept="image/*"
                          label="Select Image"
                          :required="!useDefaultImage"
                          class="mt-3"
                          @update:model-value="handleImageChange"
                          hint="Select an image file (JPG, PNG, or WebP)"
                          persistent-hint
                          variant="outlined"
                          density="comfortable"
                      />
                  </v-slide-y-transition>
              </div>

              <!-- Quantity Selector (moved below image selection) -->
              <div v-if="creationMode === 'multiple'" class="mb-4">
                  <p class="text-body-1 font-medium mb-2">Number of Ducks:</p>
                  <v-text-field
                      v-model.number="quantity"
                      type="number"
                      label="Quantity"
                      min="1"
                      :rules="[
                          v => !!v || 'Quantity is required',
                          v => v >= 1 || 'Quantity must be at least 1',
                          v => Number.isInteger(Number(v)) || 'Quantity must be a whole number',
                          v => v <= remainingDucksAllowed || `Maximum of ${remainingDucksAllowed} ducks can be added`
                      ]"
                      variant="outlined"
                      inputmode="numeric"
                      pattern="[0-9]*"
                  ></v-text-field>
              </div>

              <!-- Duck Limit Information (moved to the very bottom of the page) -->
              <v-divider class="my-4"></v-divider>
              <div v-if="props.cruiseInfo" class="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mt-4 mb-6">
                  <p class="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Duck Quota</p>
                  <div class="flex justify-between mb-2">
                      <span>Created Ducks:</span>
                      <span>{{ props.cruiseInfo.ducksCreated || 0 }}</span>
                  </div>
                  <div class="flex justify-between mb-2">
                      <span>Maximum Allowed:</span>
                      <span>{{ props.cruiseInfo.maxDucksAllowed || 0 }}</span>
                  </div>
                  <div class="flex justify-between font-medium">
                      <span>Remaining:</span>
                      <span>{{ remainingDucksAllowed }}</span>
                  </div>
                  
                  <!-- Add a progress bar for visual representation -->
                  <div class="mt-3 relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div 
                          class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all"
                          :style="`width: ${calculatePercentage()}%`"
                      ></div>
                  </div>
              </div>
          </v-form>
        </div>

        <!-- Fixed action buttons at bottom -->
        <div class="action-buttons">
            <v-btn variant="outlined" @click="$emit('close')">Cancel</v-btn>
            <v-btn
                type="submit"
                color="primary"
                :loading="loading"
                :disabled="isSubmitDisabled"
                @click="handleSubmit"
            >
                {{ submitButtonText }}
            </v-btn>
        </div>

        <v-snackbar
            v-model="showNotification"
            :color="notificationColor"
            :timeout="3000"
            location="top"
        >
            {{ notificationText }}
        </v-snackbar>
    </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import { Constant } from '@/config/constants';

interface DuckFormData {
    cruiseId: number;
    image: string;
}

interface MultipleDucksFormData {
    cruiseId: number;
    quantity: number;
    image: string;
}

const props = defineProps({
    cruiseId: {
        type: Number,
        required: true
    },
    cruiseInfo: {
        type: Object,
        default: null
    },
    warningMessage: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['close', 'created']);

// Form state
const loading = ref(false);
const imageFile = ref<File | null>(null);
const imagePreview = ref<string | null>(null);
const showNotification = ref(false);
const notificationText = ref('');
const notificationColor = ref('');
const creationMode = ref('single'); // 'single' or 'multiple'
const quantity = ref(1);
const useDefaultImage = ref(true); // Default to using default image

// Default image URL
const DEFAULT_DUCK_IMAGE = '/ducksImages/duckIcon.webp';

// Form data
const formData = ref<DuckFormData>({
    cruiseId: props.cruiseId,
    image: ''
});

// Calculate remaining ducks allowed
const remainingDucksAllowed = computed(() => {
    if (!props.cruiseInfo) return Infinity;
    const created = props.cruiseInfo.ducksCreated || 0;
    const max = props.cruiseInfo.maxDucksAllowed || 0;
    return Math.max(0, max - created);
});

// Computed properties
const isSubmitDisabled = computed(() => {
    if (useDefaultImage.value) {
        if (creationMode.value === 'multiple' && (!quantity.value || quantity.value < 1)) return true;
        if (remainingDucksAllowed.value === 0) return true;
        if (creationMode.value === 'multiple' && quantity.value > remainingDucksAllowed.value) return true;
        return false;
    }
    if (!imageFile.value) return true;
    if (creationMode.value === 'multiple' && (!quantity.value || quantity.value < 1)) return true;
    if (creationMode.value === 'multiple' && quantity.value > remainingDucksAllowed.value) return true;
    if (remainingDucksAllowed.value === 0) return true;
    return false;
});

const submitButtonText = computed(() => {
    if (creationMode.value === 'single') {
        return loading.value ? 'Adding Duck...' : 'Add Duck';
    } else {
        return loading.value ? `Adding ${quantity.value} Ducks...` : `Add ${quantity.value} Ducks`;
    }
});

// Load default image when useDefaultImage is toggled on
const setDefaultImage = async () => {
    if (useDefaultImage.value) {
        try {
            const response = await fetch(DEFAULT_DUCK_IMAGE);
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onload = () => {
                formData.value.image = reader.result as string;
            };
            reader.readAsDataURL(blob);
        } catch (error) {
            console.error('Error loading default image:', error);
            showError('Failed to load default image');
        }
    }
};

// Watch for changes to useDefaultImage
watch(useDefaultImage, async (newValue) => {
    if (newValue) {
        await setDefaultImage();
    } else {
        // Clear image when switching back to custom image
        formData.value.image = '';
        // Don't clear imageFile so user can still see their selection
    }
});

// Watch for cruiseId changes
watch(() => props.cruiseId, (newValue) => {
    formData.value.cruiseId = newValue;
});

const handleImageChange = async (fileInput: File | File[]) => {
    if (!fileInput || (Array.isArray(fileInput) && fileInput.length === 0)) {
        imagePreview.value = null;
        return;
    }

    const file = Array.isArray(fileInput) ? fileInput[0] : fileInput;

    try {
        if (!file.type.startsWith('image/')) {
            showError('Please select an image file');
            imagePreview.value = null;
            return;
        }

        // Create preview for the image selector
        const reader = new FileReader();
        reader.onload = (e) => {
            imagePreview.value = e.target?.result as string;
            formData.value.image = e.target?.result as string;
        };
        reader.readAsDataURL(file);
    } catch (error) {
        showError('Error processing image. Please try another image.');
        console.error('Error processing image:', error);
        imagePreview.value = null;
    }
};

const showError = (message: string) => {
    notificationText.value = message;
    notificationColor.value = 'error';
    showNotification.value = true;
};

const showSuccess = (message: string) => {
    notificationText.value = message;
    notificationColor.value = 'success';
    showNotification.value = true;
};

// Add validation before submit
const validateForm = () => {
    if (!useDefaultImage.value && !formData.value.image) {
        showError('Please select an image or use the default duck icon');
        return false;
    }
    
    if (creationMode.value === 'multiple' && (!quantity.value || quantity.value < 1)) {
        showError('Please specify a valid quantity');
        return false;
    }

    if (creationMode.value === 'multiple' && quantity.value > remainingDucksAllowed.value) {
        showError(`You can only add up to ${remainingDucksAllowed.value} more ducks`);
        return false;
    }
    
    return true;
};

const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
        loading.value = true;

        if (useDefaultImage.value && !formData.value.image) {
            await setDefaultImage();
        }

        let response;
        
        if (creationMode.value === 'single') {
            // Add a single duck
            response = await axios.post(
                Constant.API_URL + Constant.ADD_DUCK,
                formData.value
            );
            showSuccess('Duck added successfully!');
        } else {
            // Add multiple ducks
            const multipleFormData: MultipleDucksFormData = {
                cruiseId: props.cruiseId,
                quantity: quantity.value,
                image: formData.value.image
            };
            
            response = await axios.post(
                `${Constant.API_URL}${Constant.ADD_MULTIPLE_DUCKS}`,
                multipleFormData
            );
            showSuccess(`${quantity.value} ducks added successfully!`);
        }
        
        if (response.status === 200) {
            emit('created');
            emit('close');
        }
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            showError('Failed to add ducks. You may have reached your duck limit.');
        } else {
            showError('Failed to add duck(s). Please try again.');
        }
        console.error('Error adding duck(s):', error);
    } finally {
        loading.value = false;
    }
};

// Add this function to calculate percentage for the progress bar
const calculatePercentage = () => {
    if (!props.cruiseInfo) return 0;
    const created = props.cruiseInfo.ducksCreated || 0;
    const max = props.cruiseInfo.maxDucksAllowed || 0;
    if (max === 0) return 0;
    return Math.min(100, (created / max) * 100);
};

// Initialize the component
onMounted(async () => {
    // Set default image on mount since useDefaultImage is true by default
    await setDefaultImage();
});
</script>

<style scoped>
/* Remove arrows from number input */
input[type=number]::-webkit-inner-spin-button, 
input[type=number]::-webkit-outer-spin-button { 
    -webkit-appearance: none; 
    margin: 0; 
}
input[type=number] {
    -moz-appearance: textfield;
}

/* Mobile-friendly dialog structure */
.add-duck-form {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
}

.sticky-header {
  position: sticky;
  top: 0;
  background: inherit;
  z-index: 5;
  padding-top: 16px;
  padding-bottom: 16px;
  margin-top: -16px; /* Account for card padding */
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-scroll-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  padding-bottom: 80px; /* Space for fixed buttons */
  overscroll-behavior: contain; /* Prevent parent scroll while this is active */
}

.action-buttons {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: inherit;
  z-index: 5;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
  margin-left: -16px;
  margin-right: -16px;
  margin-bottom: -16px; /* Account for card padding */
  padding-left: 16px;
  padding-right: 16px;
}

/* Fix button touch targets for mobile */
:deep(.v-btn) {
  min-height: 48px !important;
  touch-action: manipulation;
}

/* Improve toggle button usability on mobile */
.toggle-container {
  touch-action: manipulation;
  user-select: none;
}

.toggle-btn {
  min-height: 48px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Ensure height of toggle buttons */
:deep(.v-btn-toggle .v-btn) {
  min-height: 48px;
}

/* Style for multiple duck icons in the button */
.duck-icon-group {
    display: inline-block;
    position: relative;
    width: 24px;
    height: 24px;
}

.duck-icon-small {
    position: absolute;
    font-size: 14px !important;
    width: 14px !important;
    height: 14px !important;
}

.duck-icon-right {
    top: -2px;
    right: -10px;
}

/* We no longer need the mode-specific-content styles since we moved the quantity selector */
.mode-specific-content {
    display: none;
}

/* Image selector styles */
.image-selector-container {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-bottom: 10px;
}

.image-option {
    width: 120px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
    border-radius: 10px;
    overflow: hidden;
    padding: 8px;
    touch-action: manipulation;
}

.image-option:hover {
    transform: translateY(-5px);
    background-color: rgba(var(--v-theme-primary-rgb), 0.05);
}

.image-option.selected {
    border-color: var(--v-theme-primary);
    background-color: rgba(var(--v-theme-primary-rgb), 0.1);
}

.image-preview {
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
    border-radius: 8px;
    background-color: #f5f5f5;
    overflow: hidden;
}

.dark .image-preview {
    background-color: rgba(30, 41, 59, 0.5);
}

.preview-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.upload-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.image-label {
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
}

.custom-preview {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Updated styles for the progress bar */
.progress-bar {
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background-color: #e2e8f0;
}

.progress-bar-fill {
  height: 100%;
  transition: width 0.6s ease;
}

/* Additional mobile optimizations */
@media (max-width: 600px) {
  .image-selector-container {
    gap: 12px;
  }
  
  .image-option {
    width: 110px;
  }
  
  .image-preview {
    height: 90px;
  }
}

/* Fix iOS form elements */
@supports (-webkit-touch-callout: none) {
  input, textarea, select {
    font-size: 16px !important; /* Prevent iOS zoom on focus */
  }
  
  .form-scroll-container {
    -webkit-overflow-scrolling: touch;
  }
}
</style>
